import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useVoiceChat } from '../contexts/VoiceChatContext';
import {
    VoiceRoomHeader,
    ParticipantGrid,
    VoiceControls,
    ChatOverlay,
    Participant
} from './VoiceComponents';
import StreamGifts, { Gift } from './StreamGifts';
import { getImageUrl } from '../services/api';
import { SafeImage } from './SafeImage';
import { useAuth } from '../hooks/useAuth';

const VoiceRoomSession: React.FC = () => {
    const { user } = useAuth();
    const {
        selectedRoom,
        isInRoom,
        isMinimized,
        toggleMinimize,
        leaveRoom,
        isMuted,
        toggleMute,
        isChatVisible,
        setIsChatVisible,
        chatMessages,
        sendMessage,
        sendGift,
        removeSpeaker,
        userBalance,
        isVideoEnabled,
        videoRef,
        videoStream,
        requestToSpeak,
        acceptSpeakerRequest,
        rejectSpeakerRequest
    } = useVoiceChat();

    const [showGiftPanel, setShowGiftPanel] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
    const [newMessage, setNewMessage] = useState('');

    const userId = user?.id || (user as any)?._id;
    const isSpeaker = selectedRoom?.participants.some(p => p.id === userId);
    const isHost = selectedRoom?.host === (user?.username || 'Sen');

    if (!isInRoom || !selectedRoom) return null;

    // Mini Player View
    if (isMinimized) {
        // Use the same target as full screen to ensure it stays in the phone frame
        const miniTarget = document.getElementById('modal-root') || document.body;

        return ReactDOM.createPortal(
            <div
                onClick={toggleMinimize} // Make the whole card clickable to restore
                style={{
                    position: 'absolute', // Absolute relative to modal-root (phone frame)
                    bottom: '100px', // Above bottom nav
                    right: '16px',
                    width: '90px', // Slightly wider
                    height: '130px', // Taller to fit controls comfortably
                    background: 'linear-gradient(145deg, #0f0f13, #18181b)', // Solid elegant background
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    zIndex: 9999,
                    pointerEvents: 'auto', // Allow clicks
                    overflow: 'hidden',
                    borderRadius: '20px', // Softer corners
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: 'float 6s ease-in-out infinite',
                    cursor: 'pointer' // Indicate clickable
                }}>
                {/* Visualizer / Avatar Area */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    background: 'radial-gradient(circle at center, rgba(255, 0, 127, 0.15) 0%, transparent 70%)'
                }}>
                    <div style={{
                        fontSize: '28px', // Larger avatar
                        filter: 'drop-shadow(0 0 10px rgba(255, 0, 127, 0.5))',
                        marginBottom: '8px',
                        transform: 'scale(1.1)'
                    }}>
                        {selectedRoom.hostAvatar.startsWith('http') ? (
                            <SafeImage src={selectedRoom.hostAvatar.startsWith('http') ? selectedRoom.hostAvatar : undefined} fallbackSrc={`https://ui-avatars.com/api/?name=${selectedRoom.host}&background=random`} alt="host" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                            selectedRoom.hostAvatar
                        )}
                    </div>
                    {/* Audio Wave Visualizer Simulation */}
                    <div style={{ display: 'flex', gap: '3px', height: '12px', alignItems: 'end' }}>
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} style={{
                                width: '4px',
                                height: '100%',
                                background: '#ff007f',
                                borderRadius: '2px',
                                animation: `equalizer 1s infinite ${i * 0.1}s`
                            }} />
                        ))}
                    </div>
                </div>

                {/* Controls overlay */}
                <div style={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.3)',
                    backdropFilter: 'blur(5px)',
                    borderTop: '1px solid rgba(255,255,255,0.05)'
                }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                        style={{
                            background: isMuted ? 'rgba(239, 83, 80, 0.2)' : 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            cursor: 'pointer',
                            padding: 0,
                            color: '#fff',
                            transition: 'all 0.2s'
                        }}>
                        {isMuted ? '🔇' : '🎙️'}
                    </button>
                </div>

                {/* Global Style for animations if not present */}
                <style>{`
                    @keyframes equalizer {
                        0%, 100% { height: 4px; }
                        50% { height: 100%; }
                    }
                `}</style>
            </div>,
            miniTarget
        );
    }

    // Full Screen View
    const target = document.getElementById('modal-root') || document.body;
    const isModalRoot = target.id === 'modal-root';

    return ReactDOM.createPortal(
        <div style={{
            position: isModalRoot ? 'absolute' : 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #0f0f13 0%, #050505 100%)',
            pointerEvents: 'auto',
        }}>
            {/* Header */}
            <div style={{ position: 'relative' }}>
                <VoiceRoomHeader room={selectedRoom} onLeave={toggleMinimize} />
            </div>

            {/* Main Content Area */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

                {/* Video View */}
                {isVideoEnabled && (
                    <div style={{ padding: '20px', height: '300px' }}>
                        <div style={{ width: '100%', height: '100%', background: '#000', borderRadius: '16px', overflow: 'hidden', position: 'relative' }}>
                            <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '5px 10px', borderRadius: '10px' }}>Yayındasın</div>
                        </div>
                    </div>
                )}

                {/* Speaker Requests (Host only) - Moved above grid to prevent overlap with chat */}
                {isHost && selectedRoom.speakerRequests && selectedRoom.speakerRequests.length > 0 && (
                    <div style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.05)', margin: '10px 20px', borderRadius: '12px' }}>
                        <div style={{ fontSize: '13px', color: '#ff007f', marginBottom: '8px', fontWeight: 'bold' }}>Konuşmacı İstekleri ({selectedRoom.speakerRequests.length})</div>
                        {selectedRoom.speakerRequests.map(req => (
                            <div key={req.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <SafeImage src={req.profileImage ? getImageUrl(req.profileImage) : (req.avatar ? getImageUrl(req.avatar) : undefined)} fallbackSrc={`https://ui-avatars.com/api/?name=${req.username}&background=random`} alt="avatar" style={{ width: '30px', height: '30px', borderRadius: '50%' }} />
                                    <span style={{ fontSize: '14px', color: '#fff' }}>{req.username}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button onClick={() => acceptSpeakerRequest(req.id)} style={{ background: 'rgba(76, 175, 80, 0.2)', border: '1px solid #4CAF50', color: '#4CAF50', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', cursor: 'pointer', zIndex: 100 }}>Kabul Et</button>
                                    <button onClick={() => rejectSpeakerRequest(req.id)} style={{ background: 'rgba(244, 67, 54, 0.2)', border: '1px solid #F44336', color: '#F44336', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', cursor: 'pointer', zIndex: 100 }}>Reddet</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Participants Grid */}
                <ParticipantGrid
                    room={selectedRoom}
                    isInRoom={isInRoom}
                    isSpeaker={isSpeaker}
                    onJoin={() => { 
                        requestToSpeak();
                        alert("Konuşmacı olarak katılma isteği gönderildi. Yöneticinin onayı bekleniyor.");
                    }} 
                    onInvite={() => setShowInviteModal(true)}
                    selectedParticipantId={selectedParticipantId}
                    onSelectParticipant={setSelectedParticipantId}
                />

                {isHost && selectedParticipantId && selectedParticipantId !== userId && (
                    <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'center', marginTop: '-10px', marginBottom: '10px' }}>
                        <button 
                            onClick={() => {
                                removeSpeaker(selectedParticipantId);
                                setSelectedParticipantId(null);
                            }}
                            style={{ 
                                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)', 
                                color: '#fff', 
                                border: 'none', 
                                padding: '10px 24px', 
                                borderRadius: '20px', 
                                fontWeight: 'bold',
                                fontSize: '14px',
                                boxShadow: '0 4px 15px rgba(244, 67, 54, 0.4)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <span style={{ fontSize: '16px' }}>👢</span> Misafiri Çıkar
                        </button>
                    </div>
                )}


                {/* Chat Overlay */}
                <ChatOverlay
                    messages={chatMessages}
                    isVisible={isChatVisible}
                    newMessage={newMessage}
                    onNewMessageChange={setNewMessage}
                    onSendMessage={() => {
                        sendMessage(newMessage);
                        setNewMessage('');
                    }}
                />
            </div>

            {/* Bottom Controls */}
            <VoiceControls
                isMuted={isMuted}
                toggleMute={toggleMute}
                isChatVisible={isChatVisible}
                toggleChat={() => setIsChatVisible(!isChatVisible)}
                onGift={() => setShowGiftPanel(true)}
                onLeave={leaveRoom}
                isSpeaker={isSpeaker}
            // onToggleRole={handleToggleRole} - To be implemented in context actions if robust logic needed
            />

            {/* Modals */}
            {showGiftPanel && (
                <StreamGifts
                    isOpen={showGiftPanel}
                    onClose={() => setShowGiftPanel(false)}
                    onSendGift={sendGift}
                    balance={userBalance} // From context
                    recipientName={selectedRoom.participants.find(p => p.id === selectedParticipantId)?.username}
                    onBuyCoins={() => {
                        setShowGiftPanel(false);
                        toggleMinimize(); // Minimize to navigate
                        // Navigation handled by caller usually, but here we might need a prop or global nav hook
                        // For now, prompt user to go to wallet
                    }}
                />
            )}

            {showInviteModal && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.85)',
                    backdropFilter: 'blur(5px)',
                    zIndex: 10001,
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
                }} onClick={() => setShowInviteModal(false)}>
                    <div style={{ 
                        background: 'var(--bg)', 
                        padding: '24px', 
                        borderTopLeftRadius: '24px', 
                        borderTopRightRadius: '24px', 
                        width: '100%', 
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        animation: 'slideUp 0.3s ease-out'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, margin: '0 auto 20px' }} />
                        <h3 style={{ color: '#fff', marginTop: 0, marginBottom: '20px', textAlign: 'center', fontSize: '18px', fontWeight: '600' }}>Arkadaşlarını Davet Et</h3>
                        
                        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                            <button onClick={() => {
                                navigator.clipboard.writeText(`https://buddylive.com/room/${selectedRoom.id}`);
                                alert("Oda bağlantısı kopyalandı!");
                                setShowInviteModal(false);
                            }} style={{
                                flex: 1, padding: '14px', borderRadius: '12px',
                                background: 'linear-gradient(135deg, rgba(255,42,133,0.2), rgba(233,64,87,0.2))', 
                                border: '1px solid rgba(255,42,133,0.4)',
                                color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
                            }}>
                                <span style={{ fontSize: '24px' }}>🔗</span>
                                Bağlantıyı Kopyala
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>,
        target
    );
};

export default VoiceRoomSession;

